/**
 * @module LocaleResolver
 * This module provides a flexible system for resolving, saving, and managing locale preferences
 * across different storage mechanisms (cookies, user profiles, routes, etc.).
 */

import Cookies from 'js-cookie';
import { getTenantById } from 'tenants/manager';
import type { TenantId } from 'tenants/types/tenant';
import { LOCATION_EXPIRE_DAYS } from 'constants/cookies';

/**
 * Interface for implementing locale resolution strategies.
 * Each resolver is responsible for handling locale preferences in a specific context
 * (e.g., cookies, user profile, URL route).
 */
export interface LocaleResolver {
  /** Unique identifier for the resolver */
  name: string;

  /**
   * Attempts to resolve the locale from this resolver's storage mechanism
   * @param tenantId - The ID of the current tenant
   * @returns The resolved locale if found, undefined otherwise
   */
  resolve(tenantId: TenantId): string | undefined;

  /**
   * Saves the locale preference using this resolver's storage mechanism
   * @param tenantId - The ID of the current tenant
   * @param locale - The locale code to save
   */
  save?(tenantId: TenantId, locale: string): Promise<void>;

  /**
   * Clears the locale preference from this resolver's storage mechanism
   * @param tenantId - The ID of the current tenant
   */
  clear?(tenantId: TenantId): Promise<void>;
}

/**
 * Resolver that handles locale preferences stored in the user's profile.
 * Currently a placeholder for future implementation.
 * @implements {LocaleResolver}
 */
export class UserProfileResolver implements LocaleResolver {
  name = 'UserProfile';

  resolve(_tenantId: TenantId): string | undefined {
    console.warn('[UserProfileResolver] Not implemented yet.');
    return undefined;
  }

  async save(_tenantId: TenantId, _locale: string): Promise<void> {    
    console.warn('[UserProfileResolver] Not implemented yet.');
  }

  async clear(_tenantId: TenantId): Promise<void> {
    console.warn('[UserProfileResolver] Not implemented yet.');
  }
}

/**
 * Resolver that handles locale preferences stored in browser cookies.
 * Only operates in client-side context, gracefully degrades on server-side.
 * @implements {LocaleResolver}
 */
export class CookieResolver implements LocaleResolver {
  name = 'Cookie';
  private readonly COOKIE_NAME = 'locale-preference';

  resolve(_tenantId: TenantId): string | undefined {
    if (typeof window === 'undefined') return undefined;
    return Cookies.get(this.COOKIE_NAME);
  }

  async save(_tenantId: TenantId, locale: string): Promise<void> {
    if (typeof window === 'undefined') return;
    Cookies.set(this.COOKIE_NAME, locale, {
      expires: LOCATION_EXPIRE_DAYS,
      path: '/',
      secure: true,
      sameSite: 'lax',
    });
  }

  async clear(_tenantId: TenantId): Promise<void> {
    if (typeof window === 'undefined') return;
    Cookies.remove(this.COOKIE_NAME, {
      path: '/',
      secure: true,
      sameSite: 'lax',
    });
  }
}

/**
 * Resolver that extracts locale preference from the current route.
 * @implements {LocaleResolver}
 */
export class RouteResolver implements LocaleResolver {
  name = 'Route';
  
  constructor(private currentLocale?: string) {}

  resolve(_tenantId: TenantId): string | undefined {
    return this.currentLocale;
  }
}

/**
 * Resolver that provides the default locale configured for a tenant.
 * This is typically used as a fallback when no other locale preference is found.
 * @implements {LocaleResolver}
 */
export class TenantDefaultResolver implements LocaleResolver {
  name = 'TenantDefault';

  resolve(tenantId: TenantId): string {
    return getTenantById(tenantId).localization.defaultLanguage;
  }
}

/**
 * Service that orchestrates multiple locale resolvers to determine and manage the user's locale preference.
 * Resolvers are tried in order until a valid locale is found, with tenant default as the final fallback.
 */
export class LocaleResolverService {
  private static readonly RESOLVERS = [
    UserProfileResolver,
    CookieResolver,
    RouteResolver,
    TenantDefaultResolver,
  ] as const;

  private resolvers: LocaleResolver[];
  private tenant = getTenantById(this.tenantId);

  /**
   * Creates a new instance of LocaleResolverService
   * @param tenantId - The ID of the tenant to resolve locales for
   * @param currentLocale - Optional locale from the current route
   */
  constructor(private tenantId: TenantId, currentLocale?: string) {
    this.resolvers = LocaleResolverService.RESOLVERS.map(Resolver => 
      Resolver === RouteResolver ? new Resolver(currentLocale) : new Resolver()
    );
  }

  /**
   * Resolves the user's locale preference by trying each resolver in order.
   * Returns the first valid locale found, or the tenant's default locale as fallback.
   * @returns The resolved locale code
   */
  resolveLocale(): string {
    for (const resolver of this.resolvers) {
      const locale = resolver.resolve(this.tenantId);
      if (locale && this.tenant.localization.languages.includes(locale)) {
        return locale;
      }
    }
    return this.tenant.localization.defaultLanguage;
  }

  /**
   * Saves the user's locale preference using all available resolvers that support saving.
   * @param locale - The locale code to save
   * @throws Error if the locale is not supported by the tenant
   */
  async savePreference(locale: string): Promise<void> {
    if (!this.tenant.localization.languages.includes(locale)) {
      throw new Error(`Language ${locale} not supported for tenant ${this.tenantId}`);
    }
    
    await Promise.all(
      this.resolvers
        .filter((resolver): resolver is LocaleResolver & { save: NonNullable<LocaleResolver['save']> } => 
          resolver.save !== undefined
        )
        .map(resolver => resolver.save(this.tenantId, locale))
    );
  }

  /**
   * Clears the user's locale preference from all resolvers that support clearing.
   */
  async clearPreference(): Promise<void> {
    await Promise.all(
      this.resolvers
        .filter((resolver): resolver is LocaleResolver & { clear: NonNullable<LocaleResolver['clear']> } => 
          resolver.clear !== undefined
        )
        .map(resolver => resolver.clear(this.tenantId))
    );
  }

  /**
   * Checks if locale switching is enabled for the current tenant.
   * @returns true if the tenant has multiple languages and switching is enabled
   */
  isEnabled(): boolean {
    return this.tenant.localization.languages.length > 1 && (this.tenant.localization.switchingEnabled ?? false);
  }
}